import { zodResolver } from '@hookform/resolvers/zod';
import { TabContext, TabPanel } from '@mui/lab';
import { Box, Tab, Tabs } from '@mui/material';
import {
  type Dispatch,
  type SetStateAction,
  type SyntheticEvent,
  useState,
  useEffect,
} from 'react';
import { useTranslate, useRedirect, type Identifier, useNotify } from 'react-admin';
import { type UseFormReturn, type SubmitHandler, useForm, FormProvider } from 'react-hook-form';

import { companiesDataProvider } from '@/dataProvider';
import {
  CompanyEditFormMode,
  CompanyEditFormOptions,
  ICompanyItemResponseParams,
  RegistrationTypeEnum,
} from '@/types';
import {
  CompanyInfoTab,
  CompanySettingsTab,
  CompanyPspList,
} from '@/view/companies/CompaniesEdit/components/form/EditCompanyForm/FormTabs';
import { CompanyCohortsList } from '@/view/companies/CompaniesEdit/components/form/CompanyCohortsList';
import { showErrorNotification } from '@/view/companies/CompaniesEdit/components/form/EditCompanyForm/utils';
import { getCompanyFormValidationSchema } from '@/view/companies/CompaniesEdit/components/form/EditCompanyForm/validation';
import { useGetUserRole } from '@/hooks';

export type TActiveTab = 'company_info' | 'company_settings' | 'company_psp_list';
export type TSetActiveTab = Dispatch<SetStateAction<TActiveTab>>;
export type TFormMethods = UseFormReturn<ICompanyItemResponseParams>;

export interface IAddCompanyFormProps<FormValuesType> {
  editFormMode: CompanyEditFormMode;
  companyId: Identifier;
  formDefaultValues: FormValuesType;
  onChangeMode: (type: CompanyEditFormMode, options?: CompanyEditFormOptions) => void;
}

function EditCompanyForm({
  editFormMode,
  companyId,
  formDefaultValues,
  onChangeMode,
}: IAddCompanyFormProps<ICompanyItemResponseParams>): JSX.Element {
  const t = useTranslate();
  const redirect = useRedirect();
  const notify = useNotify();
  const userRole = useGetUserRole();

  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TActiveTab>('company_info');
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [logo, setLogo] = useState<string>('');
  const [contractUrl, setContractUrl] = useState<string>('');
  const [contractFileName, setContractFileName] = useState<string>('');

  const validationSchema = getCompanyFormValidationSchema(t);
  const methods = useForm<ICompanyItemResponseParams>({
    mode: 'all',
    criteriaMode: 'all',
    defaultValues: formDefaultValues,
    resolver: zodResolver(validationSchema),
  });
  const {
    getValues,
    // formState: { errors },
  } = methods;

  const handleResetForm = (): void => {
    methods.reset();
    setSelectedDates([]);
    setActiveTab('company_info');
    redirect('/companies');
  };

  const isNextBtnDisabled =
    !getValues('legal_name') ||
    !getValues('company_name') ||
    !getValues('admins.0.first_name') ||
    !getValues('admins.0.last_name') ||
    !getValues('admins.0.email');

  const handleChangeTab = (_event: SyntheticEvent, newValue: TActiveTab): void => {
    if (newValue === 'company_settings' && isNextBtnDisabled) {
      console.log('Form is not valid. Complete all required fields correctly.');
      return;
    }
    setActiveTab(newValue);
  };

  const onSubmit: SubmitHandler<ICompanyItemResponseParams> = async (data): Promise<void> => {
    setLoading(true);

    const pay_days = selectedDates.map(date => date.toISOString());

    let newCompanyEntity: Partial<ICompanyItemResponseParams>;

    if (userRole === RegistrationTypeEnum.SUPER_ADMIN) {
      newCompanyEntity = {
        ...data,
        pay_days,
        logo,
        contract_url: contractUrl,
        withdrawal_method: 'Card',
      };
    } else {
      newCompanyEntity = {
        max_amount: data.max_amount,
        min_amount: data.min_amount,
        withdrawal_limit: data.withdrawal_limit,
        number_of_transactions: data.number_of_transactions,
        pay_days,
      };
    }

    const result = await companiesDataProvider.editCompany(
      companyId,
      newCompanyEntity,
      () => {
        setLoading(false);
      },
      // eslint-disable-next-line
      (error: any) => {
        if (error && error?.response?.data?.message) {
          showErrorNotification(error.response.data.message, notify, setActiveTab);
        }
        setLoading(false);
      },
    );

    if (result) {
      handleResetForm();
    }
  };

  useEffect(() => {
    if (formDefaultValues.pay_days && formDefaultValues.pay_days.length) {
      const dates = formDefaultValues.pay_days.map(d => new Date(d));
      setSelectedDates(dates);
    }
  }, [formDefaultValues.pay_days]);

  useEffect(() => {
    if (formDefaultValues.logo) {
      setLogo(formDefaultValues.logo);
    }
  }, [formDefaultValues.logo]);

  useEffect(() => {
    if (formDefaultValues.contract_url) {
      setContractFileName(`${t('resources.company.form.add_company.contract')}.pdf`);
      setContractUrl(formDefaultValues.contract_url);
    }
  }, [formDefaultValues.contract_url]);

  useEffect(() => {
    if (editFormMode === 'psp_list') {
      setActiveTab('company_psp_list');
    }
  }, [editFormMode]);

  useEffect(() => {
    const defaultTab =
      userRole === RegistrationTypeEnum.SUPER_ADMIN ? 'company_info' : 'company_settings';

    setActiveTab(defaultTab);
  }, [userRole]);

  return (
    <Box>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TabContext value={activeTab || ''}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleChangeTab} aria-label="company form tabs">
                {userRole === RegistrationTypeEnum.SUPER_ADMIN && (
                  <Tab
                    label={t('resources.company.form.add_company.company_info')}
                    value="company_info"
                  />
                )}
                <Tab
                  label={t('resources.company.form.add_company.company_settings')}
                  value="company_settings"
                />
                {userRole === RegistrationTypeEnum.SUPER_ADMIN && (
                  <Tab
                    label={t('resources.company.form.add_company.company_psp_list')}
                    value="company_psp_list"
                  />
                )}
                <Tab
                  label={t('resources.company.form.add_company.cohorts_list')}
                  value="company_cohorts_list"
                />
              </Tabs>
            </Box>
            <Box sx={{ width: 975 }}>
              {userRole === RegistrationTypeEnum.SUPER_ADMIN && (
                <TabPanel value="company_info">
                  <CompanyInfoTab
                    methods={methods}
                    isNextBtnDisabled={isNextBtnDisabled}
                    setActiveTab={setActiveTab}
                    setLogo={setLogo}
                    contractUrl={contractUrl}
                    setContractUrl={setContractUrl}
                    contractFileName={contractFileName}
                    setContractFileName={setContractFileName}
                  />
                </TabPanel>
              )}
              <TabPanel value="company_settings">
                <CompanySettingsTab
                  loading={loading}
                  methods={methods}
                  setActiveTab={setActiveTab}
                  selectedDates={selectedDates}
                  setSelectedDates={setSelectedDates}
                  handleResetForm={handleResetForm}
                />
              </TabPanel>
            </Box>
            {userRole === RegistrationTypeEnum.SUPER_ADMIN && (
              <TabPanel value="company_psp_list">
                {activeTab === 'company_psp_list' && <CompanyPspList onChangeMode={onChangeMode} />}
              </TabPanel>
            )}
            <TabPanel value="company_cohorts_list">
              <CompanyCohortsList />
            </TabPanel>
          </TabContext>
        </form>
      </FormProvider>
    </Box>
  );
}

export default EditCompanyForm;
